import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';

export const conversationCallSchema = z.object({
  id: z.string(),
  direction: z.enum(['inbound', 'outbound']),
  status: z.enum(['in_progress', 'completed', 'failed']),
  attemptOutcome: z
    .enum(['no_answer', 'busy', 'cancelled', 'failed', 'no_speech'])
    .nullable()
    .optional(),
  startedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  durationMs: z.number().nullable(),
  booked: z.boolean(),
  appointmentIds: z.array(z.string()),
  handedOff: z.boolean(),
  transferred: z.boolean(),
  hasRecording: z.boolean(),
  recordingExpired: z.boolean(),
  missed: z.boolean(),
  summary: z.string().nullable().optional(),
  summaryPending: z.boolean().optional(),
});
export type ConversationCall = z.infer<typeof conversationCallSchema>;

const callTranscriptSchema = z.object({
  lines: z.array(
    z.object({
      id: z.string(),
      speaker: z.enum(['caller', 'agent']),
      text: z.string(),
      at: z.string(),
    })
  ),
});
export type CallTranscriptLine = z.infer<
  typeof callTranscriptSchema
>['lines'][number];

/** Fetch the complete transcript when a call card is expanded. */
export function useCallTranscript(
  callSid: string,
  enabled: boolean,
  live = false
) {
  return useQuery({
    queryKey: queryKeys.voiceReceptionist.transcript(callSid),
    queryFn: () =>
      apiClient.get(`voice-receptionist/calls/${callSid}/transcript`, {
        schema: callTranscriptSchema,
      }),
    enabled,
    staleTime: live ? 0 : 5 * 60_000,
    refetchInterval: live ? 2_000 : false,
  });
}

const conversationCallsSchema = z.object({
  calls: z.array(conversationCallSchema),
  callerPhone: z.string().nullable(),
  smsConversationId: z.string().nullable(),
  outboundEligibleLeadId: z.string().nullable().default(null),
});

/** Calls behind this customer, including while no transcript has arrived. */
export function useConversationCalls(conversationId: string, enabled: boolean) {
  const query = useQuery({
    queryKey: queryKeys.voiceReceptionist.calls(conversationId),
    queryFn: () =>
      apiClient.get(
        `voice-receptionist/conversations/${conversationId}/calls`,
        {
          schema: conversationCallsSchema,
        }
      ),
    enabled: enabled && !!conversationId,
    // A call in progress finishes on its own; keep the card current.
    refetchInterval: 10_000,
  });
  return {
    calls: query.data?.calls ?? [],
    callerPhone: query.data?.callerPhone ?? null,
    smsConversationId: query.data?.smsConversationId ?? null,
    outboundEligibleLeadId: query.data?.outboundEligibleLeadId ?? null,
    isLoading: query.isLoading,
  };
}

const recordingUrlSchema = z.object({ url: z.string() });

/** A short-lived link to play a call's recording; fetched on demand. */
export function useCallRecordingUrl() {
  const mutation = useMutation({
    mutationFn: (callSid: string) =>
      apiClient.get(`voice-receptionist/calls/${callSid}/recording`, {
        schema: recordingUrlSchema,
      }),
    onError: (error: Error) =>
      toast.error(error.message || 'Could not load the recording'),
  });
  return {
    loadRecording: mutation.mutate,
    loadRecordingAsync: mutation.mutateAsync,
    recordingUrl: mutation.data?.url ?? null,
    isLoadingRecording: mutation.isPending,
  };
}
